import { Paper, Stack, Typography } from '@mui/material';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { CreateAssortmentDialog } from '@retail/my-assortment/containers';
import {
  useDeleteAssortmentTags,
  useFetchAssortmentTags,
  useUpdateAssortmentTags,
} from '@retail/my-assortment/data-access';
import { AssortmentsOverview } from '@retail/my-assortment/components';
import { AssortmentTag } from '@retail/my-assortment/types';
import { mgPrisRoutes } from '@retail/mgpris/config';
import { FormProvider, useForm } from 'react-hook-form';
import { dateRangeSchema, DateRangeSchema } from '@retail/products/components';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppTFunction } from '@retail/app/i18n';
import { useDisclosure } from '@shared/hooks';
import { AddButton, DeleteDialog } from '@retail/components';
import { useCallback, useState } from 'react';
import { useToast } from '@retail/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { OrgUnitType } from '@retail/mgpris/config-types';

export function SubAssortmentsPage() {
  const t = useAppTFunction();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { orgUnitType, assortmentId, isBrandContext, selectedOrgUnitAssortment } =
    useSelectedOrgUnit();
  const includeSendEvents =
    (orgUnitType === OrgUnitType.WHOLESALE_BRAND || orgUnitType === OrgUnitType.RETAIL_BRAND) &&
    selectedOrgUnitAssortment.isSuperUser;
  const { isOpen: openCreateModal, onToggle: toggleCreateModal } = useDisclosure();
  const [tagToDelete, setTagToDelete] = useState<AssortmentTag | undefined>(undefined);

  const { data: assortmentsTagDTO = [] } = useFetchAssortmentTags({
    assortmentIds: [assortmentId],
  });
  const assortmentsTag = assortmentsTagDTO.map(AssortmentTag.fromDTO);

  const assortmentLink = mgPrisRoutes.assortment.getFullLinkPath();
  const formMethods = useForm<DateRangeSchema>({
    mode: 'onSubmit',
    resolver: yupResolver(dateRangeSchema(t)),
    defaultValues: dateRangeSchema(t).getDefault(),
  });
  const { mutateAsync: deleteAssortmentTag } = useDeleteAssortmentTags();
  const { mutateAsync: updateAssortmentTag } = useUpdateAssortmentTags();

  const handleDeleteAssortmentTag = useCallback(
    (tagId: number) =>
      deleteAssortmentTag({ urlVariables: tagId })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['assortment-tags'] });
          toast.success(
            t('myAssortment.actions.assortmentIsDeleted', { assortmentName: tagToDelete?.name })
          );
        })
        .catch((err) => {
          console.error(err);
          toast.error(t('products.errors.generic'));
        }),
    [deleteAssortmentTag, queryClient, t, tagToDelete?.name, toast]
  );

  const handleUpdateAssortmentTag = useCallback(
    (tag: AssortmentTag, field: 'sendEvents' | 'availableToMember' | 'selectableByMembers') => {
      const bodyForField = {
        sendEvents: { sendEvents: !tag.sendEvents },
        availableToMember: { visibleToMembers: !tag.visibleToMembers },
        selectableByMembers: { selectableByMembers: !tag.selectableByMembers },
      };

      updateAssortmentTag({
        urlVariables: tag.id,
        body: bodyForField[field],
      })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['assortment-tags'] });
        })
        .catch((err) => {
          console.error(err);
          toast.error(t('products.errors.generic'));
        });
    },
    [queryClient, t, toast, updateAssortmentTag]
  );

  return (
    <Paper sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 2 }}>
      <Stack flexDirection="row" alignItems="center" justifyContent="space-between" pt={3} px={4}>
        <Stack flexDirection="row" alignItems="center" gap={0.5}>
          <Typography variant="subtitle1">{assortmentsTag.length}</Typography>
          <Typography variant="body1">
            {assortmentsTag.length === 1
              ? t('myAssortment.centralAssortments.assortment').toLowerCase()
              : t('myAssortment.centralAssortments.assortments').toLowerCase()}
          </Typography>
        </Stack>
        <AddButton size="small" onClick={toggleCreateModal}>
          {t('myAssortment.centralAssortments.create')}
        </AddButton>
        <FormProvider {...formMethods}>
          {openCreateModal && (
            <CreateAssortmentDialog
              open
              assortmentId={assortmentId}
              includeViewForMemberToggle={isBrandContext}
              includeSendEvents={includeSendEvents}
              onToggle={toggleCreateModal}
            />
          )}
        </FormProvider>
      </Stack>
      <AssortmentsOverview
        assortmentsTag={assortmentsTag}
        assortmentLink={assortmentLink}
        includeSendEvents={includeSendEvents}
        isBrandContext={isBrandContext}
        deleteTag={setTagToDelete}
        updateTag={handleUpdateAssortmentTag}
      />

      {tagToDelete && (
        <DeleteDialog
          isOpen
          dialogTitle={t('myAssortment.actions.deleteAssortment')}
          message={t('myAssortment.actions.deleteAssortmentMessage', {
            assortmentName: tagToDelete.name,
          })}
          onDelete={() => handleDeleteAssortmentTag(tagToDelete.id)}
          onClose={() => setTagToDelete(undefined)}
        />
      )}
    </Paper>
  );
}
